// Web Speech API wrapper for voice task capture.
// Works in:
//  - PWA (Safari iOS 14.5+, Chrome desktop, Edge)
//  - Tauri iOS WKWebView (uses Safari Web Speech)
//  - Tauri Desktop macOS (Safari engine)
// Notes:
//  - User permission for microphone is required (prompted on first use).
//  - For iOS Tauri Mobile, Info.plist must declare NSMicrophoneUsageDescription
//    and NSSpeechRecognitionUsageDescription.

type SpeechRecognitionConstructor = new () => SpeechRecognition;

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: { error: string; message?: string }) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

function getCtor(): SpeechRecognitionConstructor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function isSpeechRecognitionSupported(): boolean {
  return getCtor() !== null;
}

export interface VoiceCaptureCallbacks {
  onPartial?: (text: string) => void;
  onFinal: (text: string) => void;
  onError?: (err: string) => void;
  onEnd?: () => void;
  onStart?: () => void;
  lang?: string; // default: "ja-JP"
}

export interface VoiceCaptureHandle {
  stop: () => void;
  abort: () => void;
}

export function startVoiceCapture(cb: VoiceCaptureCallbacks): VoiceCaptureHandle | null {
  const Ctor = getCtor();
  if (!Ctor) {
    cb.onError?.("SpeechRecognition not supported in this environment");
    return null;
  }

  const recognition = new Ctor();
  recognition.continuous = true;
  recognition.interimResults = true;
  recognition.lang = cb.lang ?? "ja-JP";

  let finalText = "";

  recognition.onstart = () => {
    cb.onStart?.();
  };

  recognition.onresult = (event: SpeechRecognitionEvent) => {
    let interim = "";
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const res = event.results[i];
      const transcript = res[0]?.transcript ?? "";
      if (res.isFinal) {
        finalText += transcript;
      } else {
        interim += transcript;
      }
    }
    const combined = (finalText + interim).trim();
    if (combined) cb.onPartial?.(combined);
  };

  recognition.onerror = (event) => {
    cb.onError?.(event.error || "unknown error");
  };

  recognition.onend = () => {
    const text = finalText.trim();
    if (text) cb.onFinal(text);
    cb.onEnd?.();
  };

  try {
    recognition.start();
  } catch (err) {
    cb.onError?.(`failed to start: ${String(err)}`);
    return null;
  }

  return {
    stop: () => {
      try {
        recognition.stop();
      } catch {
        // ignore
      }
    },
    abort: () => {
      try {
        recognition.abort();
      } catch {
        // ignore
      }
    },
  };
}

// --- Quick natural-language parsing for date hints ---
// Examples:
//   "明日 14時 〇〇に電話"       → today+1, dueDate set
//   "来週月曜日 病院"           → next Monday
//   "金曜 ミーティング"          → upcoming Friday
//   "30分後 ◯◯確認"             → today (no specific time persisted yet)
// Returns { title, dueDate (YYYY-MM-DD | null), priority? }

export interface ParsedTask {
  title: string;
  dueDate: string | null;
  priority?: "high" | "medium" | "low";
}

const WEEKDAYS_JP: Record<string, number> = {
  日: 0,
  月: 1,
  火: 2,
  水: 3,
  木: 4,
  金: 5,
  土: 6,
};

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function nextWeekday(target: number, base = new Date()): Date {
  const d = new Date(base);
  const delta = (target - d.getDay() + 7) % 7 || 7;
  d.setDate(d.getDate() + delta);
  return d;
}

export function parseVoiceText(raw: string): ParsedTask {
  let text = raw.trim();
  let dueDate: string | null = null;
  let priority: ParsedTask["priority"] | undefined;

  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  // 今日
  if (/今日/.test(text)) {
    dueDate = toIsoDate(today);
    text = text.replace(/今日/g, "");
  }
  // 明日
  else if (/明日/.test(text)) {
    const d = new Date(today);
    d.setDate(d.getDate() + 1);
    dueDate = toIsoDate(d);
    text = text.replace(/明日/g, "");
  }
  // 明後日
  else if (/明後日|あさって/.test(text)) {
    const d = new Date(today);
    d.setDate(d.getDate() + 2);
    dueDate = toIsoDate(d);
    text = text.replace(/明後日|あさって/g, "");
  }
  // 来週○曜
  else {
    const next = text.match(/来週([日月火水木金土])曜?日?/);
    if (next) {
      const target = WEEKDAYS_JP[next[1]];
      const d = nextWeekday(target);
      // 来週 = 必ず 7日以上先
      if (d.getTime() - today.getTime() < 7 * 86400 * 1000) {
        d.setDate(d.getDate() + 7);
      }
      dueDate = toIsoDate(d);
      text = text.replace(next[0], "");
    } else {
      // 今週○曜 or 単独○曜
      const week = text.match(/(?:今週)?([日月火水木金土])曜?日?/);
      if (week) {
        const target = WEEKDAYS_JP[week[1]];
        dueDate = toIsoDate(nextWeekday(target));
        text = text.replace(week[0], "");
      }
    }
  }

  // 優先度ヒント
  if (/(緊急|至急|今すぐ|大至急)/.test(text)) {
    priority = "high";
    text = text.replace(/(緊急|至急|今すぐ|大至急)/g, "");
  } else if (/(あとで|余裕|低優先|low)/i.test(text)) {
    priority = "low";
    text = text.replace(/(あとで|余裕|低優先|low)/gi, "");
  }

  // 句読点・余分なスペース除去
  text = text
    .replace(/^[、。\s]+|[、。\s]+$/g, "")
    .replace(/\s+/g, " ")
    .trim();

  return {
    title: text || raw.trim(),
    dueDate,
    priority,
  };
}
